import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Input, Button, Select, Spin, Card, Divider } from "antd";
import {
  ArrowLeftOutlined,
  PictureOutlined,
  UserOutlined,
} from "@ant-design/icons";
import Colors from "../../constants/Colors";
import { E_PRESCRIPTIONS_BASE } from "../../constants/e-prescriptions";
import { REGISTRATION_PLATFORM_DOCTOR_WEBSITE } from "../../constants/registrationPlatform";
import DataProvider from "../../apis";
import type {
  CreateDiagnosticPayload,
  LabItem,
  DiagnosticPriceItem,
  DiagnosticPaymentMethod,
} from "../../apis";
import { notification } from "antd";

const PAYMENT_OPTIONS: { value: DiagnosticPaymentMethod; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "insurance", label: "Insurance" },
  { value: "partner_company", label: "Partner company" },
];

// Partner rows come back as either a raw array or { data: [...] } — normalize
// to antd Select options keyed by the partner name (what the backend stores in
// the diagnostic `provider` field). Mirrors create-eprescription.tsx.
function partnersToOptions(raw: unknown): { label: string; value: string }[] {
  const list = Array.isArray(raw)
    ? (raw as { name: string }[])
    : ((raw as { data?: { name: string }[] })?.data ?? []);
  return list.map((p) => ({ label: p.name, value: p.name }));
}

function buildXrayNotes(values: Record<string, unknown>): string {
  const parts: string[] = [];
  if (values.age) parts.push(`Age: ${values.age}`);
  if (values.ward) parts.push(`Ward/Address: ${values.ward}`);
  if (values.clinicalHistory)
    parts.push(`Clinical history: ${values.clinicalHistory}`);
  if (values.investigationRequested)
    parts.push(`Investigation requested: ${values.investigationRequested}`);
  if (values.medicalOfficer)
    parts.push(`Medical officer: ${values.medicalOfficer}`);
  if (values.stationAddress)
    parts.push(`Station/Address: ${values.stationAddress}`);
  if (values.xraySerialNo)
    parts.push(`X-Ray serial no: ${values.xraySerialNo}`);
  if (values.previousSerialNo)
    parts.push(`Previous serial no: ${values.previousSerialNo}`);
  if (values.previousExamDetails)
    parts.push(`Previous exam details: ${values.previousExamDetails}`);
  return parts.length ? `[X-Ray]\n${parts.join("\n")}` : "[X-Ray]";
}

export default function Createxrayreport() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [labs, setLabs] = useState<LabItem[]>([]);
  const [prices, setPrices] = useState<DiagnosticPriceItem[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [insuranceOptions, setInsuranceOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [corporateOptions, setCorporateOptions] = useState<
    { label: string; value: string }[]
  >([]);
  // Drives the conditional provider select below.
  const paymentMethod = Form.useWatch("paymentmethod", form);

  const loadOptions = useCallback(async () => {
    setOptionsLoading(true);
    // Load labs and prices independently — a failure in one must not blank the
    // other (a rejected Promise.all used to leave the lab list empty whenever
    // the prices call failed).
    const [labsRes, pricesRes, insRes, corpRes] = await Promise.allSettled([
      DataProvider.getLabsQuery(1, 100),
      DataProvider.getDiagnosticPrices(),
      DataProvider.getPartners("filter=type||eq||insurance&limit=200"),
      DataProvider.getPartners("filter=type||eq||corporate&limit=200"),
    ]);

    // Provider lists are non-critical — a failure just leaves the relevant
    // select empty, it never blanks the labs/tests form.
    if (insRes.status === "fulfilled") {
      setInsuranceOptions(partnersToOptions(insRes.value.data));
    }
    if (corpRes.status === "fulfilled") {
      setCorporateOptions(partnersToOptions(corpRes.value.data));
    }

    if (labsRes.status === "fulfilled") {
      setLabs(
        (labsRes.value.data as { labs?: LabItem[]; totalPages?: number })
          ?.labs ?? [],
      );
    } else {
      console.error("Failed to load labs:", labsRes.reason);
      notification.error({ message: "Could not load labs. Try again later." });
    }

    if (pricesRes.status === "fulfilled") {
      const d = pricesRes.value.data;
      setPrices(
        Array.isArray(d)
          ? d
          : ((d as { data?: DiagnosticPriceItem[] })?.data ?? []),
      );
    } else {
      console.error("Failed to load test options:", pricesRes.reason);
      notification.error({
        message: "Could not load test options. Try again later.",
      });
    }

    setOptionsLoading(false);
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const onFinish = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      const xrayNotes = buildXrayNotes(values);
      const existingNotes = (values.notes as string)?.trim();
      const notes = existingNotes
        ? `${xrayNotes}\n\n${existingNotes}`
        : xrayNotes;

      // The test field is a tags-select: catalogue picks come back as
      // "price:<id>", free-typed customs as the raw text.
      const tests = ((values.tests as string[]) ?? [])
        .map((v) =>
          typeof v === "string" && v.startsWith("price:")
            ? { diagnosticPriceId: Number(v.slice("price:".length)) }
            : { customTest: String(v).trim() },
        )
        .filter(
          (t) =>
            t.diagnosticPriceId != null ||
            (t.customTest != null && t.customTest.length > 0),
        );

      const paymentmethod = values.paymentmethod as DiagnosticPaymentMethod;
      // provider = the selected insurance/corporate partner name; idType/idNumber
      // carry the policy or staff id. Only sent for insurance / partner_company.
      const provider = (values.provider as string)?.trim() || undefined;
      const idNumber = (values.idNumber as string)?.trim() || undefined;
      const idType =
        paymentmethod === "insurance"
          ? "policy_number"
          : paymentmethod === "partner_company"
            ? "staff_id"
            : undefined;

      const payload: CreateDiagnosticPayload = {
        fullname: (values.fullname as string)?.trim() || undefined,
        contact: (values.contact as string)?.trim() || undefined,
        paymentmethod,
        provider,
        idType,
        idNumber,
        schedule_date: values.schedule_date as string,
        schedule_time: values.schedule_time as string,
        tests,
        labId: Number(values.labId),
        notes,
        requestType: "xray",
        source: REGISTRATION_PLATFORM_DOCTOR_WEBSITE,
      };
      await DataProvider.submitDiagnostic(payload);
      notification.success({
        message: "X-Ray request created. The lab will handle scheduling.",
      });
      navigate(`${E_PRESCRIPTIONS_BASE}?tab=3`);
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? String(
              (err as { response?: { data?: { message?: string } } }).response
                ?.data?.message,
            )
          : "Failed to create X-Ray request.";
      notification.error({ message: msg || "Failed to create X-Ray request." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[60vh] bg-[#f8fafc] px-4 py-6">
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(-1)}
        className="mb-2"
        aria-label="Go back"
      >
        Back
      </Button>

      <Card className="max-w-2xl rounded-xl shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <PictureOutlined
            className="text-2xl"
            style={{ color: Colors.primary }}
            aria-hidden
          />
          <h1 className="text-xl font-semibold m-0">New X-Ray request</h1>
        </div>
        <p className="text-gray-600 text-sm mt-0 mb-6">
          Request radiology (X-Ray) for a patient. Extra clinical fields are
          stored in the request notes.
        </p>

        {optionsLoading ? (
          <div className="flex justify-center py-12">
            <Spin size="large" tip="Loading labs and tests…" />
          </div>
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            requiredMark={true}
          >
            <Divider orientation="left" plain className="text-gray-500 text-sm">
              Patient & schedule
            </Divider>
            <Form.Item
              name="fullname"
              label="Name of patient"
              rules={[{ required: true, message: "Enter patient name" }]}
            >
              <Input
                prefix={<UserOutlined className="text-gray-400" />}
                placeholder="Patient name"
                aria-label="Patient name"
              />
            </Form.Item>
            <Form.Item name="age" label="Age">
              <Input placeholder="Age" aria-label="Patient age" />
            </Form.Item>
            <Form.Item name="ward" label="Ward / Address">
              <Input
                placeholder="Ward or address"
                aria-label="Ward or address"
              />
            </Form.Item>
            <Form.Item name="contact" label="Contact (phone)">
              <Input placeholder="Phone number" aria-label="Patient contact" />
            </Form.Item>
            <Form.Item
              name="schedule_date"
              label="Preferred date"
              rules={[{ required: true, message: "Select date" }]}
            >
              <Input type="date" aria-label="Schedule date" />
            </Form.Item>
            <Form.Item
              name="schedule_time"
              label="Preferred time"
              rules={[{ required: true, message: "Enter time" }]}
            >
              <Input
                type="time"
                placeholder="e.g. 09:00"
                aria-label="Schedule time"
              />
            </Form.Item>

            <Divider orientation="left" plain className="text-gray-500 text-sm">
              Clinical details
            </Divider>
            <Form.Item name="clinicalHistory" label="Brief clinical history">
              <Input.TextArea
                rows={2}
                placeholder="Clinical history"
                aria-label="Clinical history"
              />
            </Form.Item>
            <Form.Item
              name="investigationRequested"
              label="Radiological investigation requested"
            >
              <Input.TextArea
                rows={2}
                placeholder="Investigation requested"
                aria-label="Investigation requested"
              />
            </Form.Item>
            <Form.Item name="medicalOfficer" label="Medical officer / Dr.">
              <Input
                placeholder="Medical officer name"
                aria-label="Medical officer"
              />
            </Form.Item>
            <Form.Item name="stationAddress" label="Station / Address">
              <Input
                placeholder="Station or address"
                aria-label="Station address"
              />
            </Form.Item>
            <Form.Item name="xraySerialNo" label="X-Ray serial no.">
              <Input
                placeholder="X-Ray serial no"
                aria-label="X-Ray serial number"
              />
            </Form.Item>
            <Form.Item name="previousSerialNo" label="Previous serial no.">
              <Input
                placeholder="Previous serial no"
                aria-label="Previous serial number"
              />
            </Form.Item>
            <Form.Item name="previousExamDetails" label="Previous exam details">
              <Input.TextArea
                rows={2}
                placeholder="Previous exam details"
                aria-label="Previous exam details"
              />
            </Form.Item>

            <Divider orientation="left" plain className="text-gray-500 text-sm">
              Lab & test
            </Divider>
            <Form.Item
              name="labId"
              label="Lab / center"
              rules={[{ required: true, message: "Select a lab" }]}
            >
              <Select
                placeholder="Select lab"
                allowClear
                showSearch
                optionFilterProp="label"
                options={labs.map((l) => ({
                  value: l.id,
                  label: `${l.name} (${l.center})`,
                }))}
                aria-label="Select lab"
              />
            </Form.Item>
            <Form.Item
              name="tests"
              label="Tests / diagnostics"
              extra="Pick one or more from the list. If a study isn't listed, type its exact name and press Enter to add it."
              rules={[
                {
                  validator: (_, value) =>
                    value && (value as string[]).length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error("Add at least one test")),
                },
              ]}
            >
              <Select
                mode="tags"
                placeholder="Select or type tests"
                showSearch
                optionFilterProp="label"
                options={prices.map((p) => ({
                  value: `price:${p.id}`,
                  // label: `${p.name} — ${p.laboratory} (${p.cost ? `¢${p.cost}` : "—"})`,
                  label: `${p.name}`,
                }))}
                filterOption={(input, option) =>
                  ((option?.label as string) ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                aria-label="Select or type diagnostic tests"
              />
            </Form.Item>
            <Form.Item
              name="paymentmethod"
              label="Payment method"
              rules={[{ required: true, message: "Select payment method" }]}
            >
              <Select
                placeholder="Select payment method"
                options={PAYMENT_OPTIONS}
                aria-label="Payment method"
                onChange={() =>
                  form.setFieldsValue({ provider: undefined, idNumber: undefined })
                }
              />
            </Form.Item>

            {paymentMethod === "insurance" && (
              <>
                <Form.Item
                  name="provider"
                  label="Insurance provider"
                  rules={[
                    { required: true, message: "Select insurance provider" },
                  ]}
                >
                  <Select
                    placeholder="Select insurance provider"
                    options={insuranceOptions}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    notFoundContent="No providers found"
                    aria-label="Insurance provider"
                  />
                </Form.Item>
                <Form.Item
                  name="idNumber"
                  label="Policy number"
                  rules={[{ required: true, message: "Enter policy number" }]}
                >
                  <Input
                    placeholder="e.g. NHIS-0012345"
                    aria-label="Policy number"
                  />
                </Form.Item>
              </>
            )}

            {paymentMethod === "partner_company" && (
              <>
                <Form.Item
                  name="provider"
                  label="Partner company"
                  rules={[
                    { required: true, message: "Select partner company" },
                  ]}
                >
                  <Select
                    placeholder="Select partner company"
                    options={corporateOptions}
                    showSearch
                    allowClear
                    optionFilterProp="label"
                    notFoundContent="No partner companies found"
                    aria-label="Partner company"
                  />
                </Form.Item>
                <Form.Item
                  name="idNumber"
                  label="Staff ID"
                  rules={[{ required: true, message: "Enter staff ID" }]}
                >
                  <Input
                    placeholder="Staff/employee ID"
                    aria-label="Staff ID"
                  />
                </Form.Item>
              </>
            )}
            <Form.Item name="notes" label="Additional notes">
              <Input.TextArea
                rows={2}
                placeholder="Any other notes"
                aria-label="Additional notes"
              />
            </Form.Item>

            <Form.Item className="mb-0 mt-8">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                style={{ backgroundColor: Colors.primary }}
                aria-label="Submit X-Ray request"
              >
                {loading ? "Submitting…" : "Submit X-Ray request"}
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
}
